<script lang="ts" setup>
import { InputAutoCompletion } from '@sancare/typescript-commons'
import {
  useAutoCompletionDebouncedWatcher
} from '@sancare/typescript-commons/src/misc/composables/useAutoCompletionDebouncedWatcher'
import { capitalize, lowerFirst } from '@sancare/typescript-commons/src/misc/string-utils'
import { FilterDataValue, FilterEvent } from '@sancare/typescript-commons/src/types/filters'
import { Dayjs } from 'dayjs'
import _ from 'lodash'
import { computed, onMounted, Ref, ref, watch } from 'vue'

import { useFilterOptions } from '@/filters/composables/useFilterOptions'
import { useStore } from '@/store'
import { FilterData } from '@/store/modules/filters/types'
import { Criterion } from '@/store/modules/filters/types'
import { PmsiTypeEnum } from '@/store/modules/settings/types'

import { optionToValue } from './utils'

const { displayedOption } = useFilterOptions()

const store = useStore()

const props = withDefaults(defineProps<{
  filterData: FilterData
  initialValue?: FilterDataValue | null
  pmsiType: PmsiTypeEnum
  contextDate?: Dayjs | null
}>(), {
  initialValue: null,
  contextDate: null,
})

const emit = defineEmits<{
  (e: 'add-filter', filter: FilterEvent): void
}>()

const errorMsg = ref('')
const showCompletion = ref(false)
const typedInput: Ref<string> = ref('')

const pmsiCountry = computed(() => store.state.settings.pmsi.country)
const visibleSubFilters = computed(() =>
  props.filterData.subFilters?.filter((filter) =>
    (!filter.pmsiTypes || filter.pmsiTypes.includes(props.pmsiType))
        && (!filter.country || filter.country.includes(pmsiCountry.value))
  )
)
const completionItems = computed(() => store.state.remoteLists.completionItems)
const completionRequest = computed(() => store.state.remoteLists.completionRequest)
const hasCurrentSearchRevaloDasCriteria = computed(() =>
  store.state.stayList.currentSearch.globalCriteriaGroup?.criteriaList.some(
    (criterion: Criterion) => criterion.type === 'revaloDAS'
  )
)
const hasCurrentSearchRevaloDpCriteria = computed(() =>
  store.state.stayList.currentSearch.globalCriteriaGroup?.criteriaList.some(
    (criterion: Criterion) => criterion.type === 'revaloDP'
  )
)

const autoCompleteItemTitleProp = computed(() => {
  if (props.filterData.name === 'drug') {
    return 'atc'
  }
  if (props.filterData.name === 'biology') {
    return 'code'
  }
  return 'reference'
})
const autoCompleteItemDescriptionProp = computed(() => {
  if (props.filterData.name === 'drug') {
    return 'atcLabel'
  }
  if (props.filterData.name === 'biology') {
    return 'title'
  }
  return 'description'
})

const subFiltersValues = ref<Record<string, string>>({})

const subFilters = computed(() => {
  if (!visibleSubFilters.value) {
    return []
  }
  if (props.filterData.name !== 'revaloDiagnosis') {
    return visibleSubFilters.value
  }

  // prevent selecting revaloDAS and revaloDP in same search
  const subFilters = []
  const diagTypeInitialOptions = _.find(visibleSubFilters.value, (subFilter) => subFilter.name === 'diagType').options
  _.forEach(visibleSubFilters.value, (subFilter) => {
    if (subFilter.name === 'diagType') {
      subFilter.options = diagTypeInitialOptions
      if (hasCurrentSearchRevaloDasCriteria.value) {
        _.remove(subFilter.options, (option) => option === 'DP')
      }
      if (hasCurrentSearchRevaloDpCriteria.value) {
        _.remove(subFilter.options, (option) => option === 'DAS')
      }
    }
    subFilters.push(subFilter)
  })
  return subFilters
})

function initializeSubFiltersDefaults() {
  if (!visibleSubFilters.value) return

  visibleSubFilters.value.forEach((filter) => {
    let value = ''

    if (filter.default) {
      value = filter.default
    } else if (filter.options?.length) {
      value = optionToValue(filter.options[0])
    }

    subFiltersValues.value[filter.name] = value
  })
}

function openCompletion() {
  showCompletion.value = true
}

function closeCompletion() {
  showCompletion.value = false
}

function selectItem(completionItem) {
  const name = props.filterData.name
  let type = props.filterData.format
  const value = props.filterData.name !== 'drug' ? completionItem.reference : completionItem.atc
  const description = props.filterData.name === 'medicalAct' ? completionItem.description : ''

  type = type.replace('{filterType}', capitalize(props.filterData.name))
  if (visibleSubFilters.value) {
    visibleSubFilters.value.forEach((filter) => {
      type = type.replace(`{${filter.name}}`, capitalize(subFiltersValues.value[filter.name]))
    })
  }
  type = lowerFirst(type)

  closeCompletion()
  emit('add-filter', { name, type, value })
  typedInput.value = ''
}

function addFilter() {
  const name = props.filterData.name
  let type = props.filterData.format
  const value = typedInput.value.trim()

  if (props.filterData.name === 'revaloDiagnosis' && value === '*') {
    errorMsg.value = 'Impossible d\'ajouter ce critère'
    return
  }

  if (value.length) {
    type = type.replace('{filterType}', capitalize(props.filterData.name))
    if (visibleSubFilters.value) {
      visibleSubFilters.value.forEach((filter) => {
        type = type.replace(`{${filter.name}}`, capitalize(subFiltersValues.value[filter.name]))
      })
    }
    type = lowerFirst(type)
    closeCompletion()
    emit('add-filter', { name, type, value })
  }
  typedInput.value = ''
}

function fetchCompletionItems(search: string, signal: AbortSignal) {
  errorMsg.value = ''
  store.dispatch('remoteLists/getCompletionItems', {
    url: '/api/stay-search-criteria/completion',
    urlParams: {
      type: props.filterData.completionName,
      search,
      ...(props.contextDate ? { contextDate: props.contextDate.format('YYYY-MM-DD') } : {}),
    },
    signal,
  })
}

function clearCompletionItems() {
  store.commit('remoteLists/clearCompletionItems')
  errorMsg.value = ''
}

function initializeFromInitialValue() {
  if (!visibleSubFilters.value) return

  visibleSubFilters.value.forEach((filter) => {
    const initial = props.initialValue?.subFilters?.[filter.name]

    let value = initial ?? filter.default

    if (value === null) {
      value = filter.options?.length
        ? optionToValue(filter.options[0])
        : ''
    }

    subFiltersValues.value[filter.name] = value
  })

  if (props.initialValue?.value) {
    typedInput.value = props.initialValue.value
  }
}


useAutoCompletionDebouncedWatcher(typedInput, completionItems, fetchCompletionItems, clearCompletionItems)

watch(() => props.filterData, () => {
  clearCompletionItems()
  typedInput.value = ''
})

watch(
  visibleSubFilters,
  () => {
    if (!props.initialValue) {
      initializeSubFiltersDefaults()
    }
  },
  { immediate: true }
)

onMounted(() => initializeFromInitialValue())

watch(
  () => props.initialValue,
  () => {
    initializeFromInitialValue()
    closeCompletion()
  },
  { immediate: true }
)
</script>

<template>
  <div>
    <div class="row g-2">
      <div
        v-if="filterData && subFilters.length"
        class="col-6"
      >
        <div class="input-group">
          <select
            v-for="filter in subFilters"
            :id="`completion-filter-sub-${filter.name}`"
            :key="filter.name"
            v-model="subFiltersValues[filter.name]"
            class="form-select"
          >
            <option
              v-for="opt in filter.options"
              :key="opt"
              :value="opt"
            >
              {{ displayedOption(filter, opt) }}
            </option>
          </select>
        </div>
      </div>
      <div
        :class="subFilters.length ? ['col-6'] : ['col-12']"
      >
        <div class="input-group">
          <input
            id="completion-filter-search"
            v-model="typedInput"
            v-on-click-outside="closeCompletion"
            type="text"
            class="form-control"
            @click="openCompletion"
            @keydown.esc="closeCompletion"
          >
          <div class="input-group-append">
            <button
              id="completion-filter-add"
              class="btn btn-primary"
              type="button"
              @click="addFilter"
            >
              Ok
            </button>
          </div>
        </div>
      </div>
      <div
        v-if="errorMsg"
        class="col-12 text-end g-0"
      >
        <span class="text-danger pt-1">{{ errorMsg }}</span>
      </div>
    </div>
    <input-auto-completion
      v-show="showCompletion && typedInput !== ''"
      :completion-list="completionItems"
      :is-loading="completionRequest.fetching"
      :title-prop="autoCompleteItemTitleProp"
      :description-prop="autoCompleteItemDescriptionProp"
      @select-item="selectItem"
    />
  </div>
</template>
